import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import type { LeadRejectionReason } from "@/lib/db-types";
import type { PoolLeadStatus, PoolState } from "@/lib/lead-pool";

/** Hvor leadene i hver pool er (RPC pool_overview, VIB-3050). */
export interface PoolCounts {
  list_id: string;
  total: number;
  free: number;
  queued: number;
  resting: number;
  meeting: number;
  customer: number;
  rejected: number;
}

export function usePoolOverview() {
  return useQuery({
    queryKey: ["pool-overview"],
    queryFn: async (): Promise<PoolCounts[]> => {
      const { data, error } = await supabase.rpc("pool_overview");
      if (error) throw new Error(error.message);
      return (data as unknown as PoolCounts[]) ?? [];
    },
    staleTime: 30 * 1000,
  });
}

export interface PoolLeadRow extends PoolLeadStatus {
  id: string;
  name: string;
  organization_number: string;
  industry_description: string | null;
  municipality: string | null;
  segment: string | null;
  priority: string | null;
  status: string;
  last_attempt_at: string | null;
}

/** Leads i en pool for admin (høyst 1000), med søk og filter på tilstand. */
export function usePoolLeads(listId: string | null, search: string, state: PoolState | null) {
  const term = search.trim();
  return useQuery({
    queryKey: ["pool-leads", listId, { search: term, state }],
    queryFn: async (): Promise<PoolLeadRow[]> => {
      const { data, error } = await supabase.rpc("pool_leads", { p_list_id: listId!, p_search: term || null, p_state: state });
      if (error) throw new Error(error.message);
      return (data as unknown as PoolLeadRow[]) ?? [];
    },
    enabled: !!listId,
  });
}

export interface TimelineItem {
  at: string;
  kind: "attempt" | "event";
  outcome?: string;
  note?: string | null;
  event_type?: string;
  source?: string;
  user_name: string | null;
  payload?: Record<string, unknown>;
}

export interface LeadDetail {
  lead: PoolLeadStatus & {
    id: string;
    name: string;
    organization_number: string;
    industry_description: string | null;
    municipality: string | null;
    employees: number | null;
    revenue_nok: number | null;
    pitch: string | null;
    notes: string | null;
    priority: string | null;
    status: string;
    max_attempts?: number;
  };
  state: PoolState;
  list_id: string;
  list_name: string;
  holder_name: string | null;
  second_try: boolean;
  call_back_at: string | null;
  contacts: { id: string; full_name: string; title: string | null; email: string | null; phone: string | null }[];
  timeline: TimelineItem[];
}

/** Leadkort for admin: lead, plassering, kontakter og tidslinje. */
export function useLeadDetail(leadId: string | null) {
  return useQuery({
    queryKey: ["lead-detail", leadId],
    queryFn: async (): Promise<LeadDetail | null> => {
      const { data, error } = await supabase.rpc("lead_detail", { p_lead_id: leadId! });
      if (error) throw new Error(error.message);
      return (data as unknown as LeadDetail) ?? null;
    },
    enabled: !!leadId,
  });
}

export type AdminLeadAction =
  | { action: "assign"; userId: string }
  | { action: "return" }
  | { action: "move"; listId: string }
  | { action: "reject"; reason: LeadRejectionReason; revisitOn: string | null };

const DONE: Record<AdminLeadAction["action"], string> = {
  assign: "tildelt",
  return: "lagt tilbake i poolen",
  move: "flyttet",
  reject: "avvist",
};

/** Tildel, tilbake til poolen, flytt eller avvis. Logges per lead med deg som aktør. */
export function useAdminLeadAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ leadIds, ...a }: AdminLeadAction & { leadIds: string[] }) => {
      const { data, error } = await supabase.rpc("admin_lead_action", {
        p_lead_ids: leadIds,
        p_action: a.action,
        p_user_id: a.action === "assign" ? a.userId : null,
        p_list_id: a.action === "move" ? a.listId : null,
        p_reason: a.action === "reject" ? a.reason : null,
        p_revisit_on: a.action === "reject" ? a.revisitOn : null,
      });
      if (error) throw new Error(error.message);
      return { count: Number(data ?? 0), action: a.action };
    },
    onSuccess: ({ count, action }) => {
      toast.success(`${count} ${count === 1 ? "lead" : "leads"} ${DONE[action]}`);
      void invalidateFor(qc, "leads", { userId: undefined });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
